import { Component } from "react";
import { WarningOctagon, ArrowClockwise } from "@phosphor-icons/react";
import { reportClientError } from "@/lib/telemetry";
import { referenceIdFor } from "@/lib/api";

/**
 * Isolates a crashing page from the application shell.
 *
 * Mounted by AppLayout around the routed page and keyed by pathname, so
 * a broken Sales Leads render shows this card while the sidebar, header
 * and every other route keep working — and navigating away resets it.
 */
export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, referenceId: null, attempt: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error, referenceId: referenceIdFor(null, "UI") };
  }

  componentDidCatch(error, info) {
    reportClientError("route_render_error", {
      message: String(error?.message || error).slice(0, 300),
      component_stack: String(info?.componentStack || "").slice(0, 800),
      reference_id: this.state.referenceId,
    });
  }

  retry = () => this.setState((s) => ({ error: null, referenceId: null, attempt: s.attempt + 1 }));

  render() {
    if (!this.state.error) {
      return <div key={this.state.attempt} className="contents">{this.props.children}</div>;
    }
    const label = this.props.label || "This page";
    return (
      <div className="p-6" data-testid="route-error-boundary">
        <div className="max-w-xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <WarningOctagon size={22} className="text-red-600 flex-shrink-0 mt-0.5" weight="fill" />
            <div className="space-y-2">
              <p className="text-sm font-bold text-[#0A0A0A]">{label} could not be displayed.</p>
              <p className="text-sm text-[#525252]">
                Something went wrong while showing this page. The rest of Pulse is still
                available — use the menu to keep working, or try again.
              </p>
              <p className="text-xs text-[#737373]">Reference: {this.state.referenceId}</p>
              <button
                onClick={this.retry}
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC]"
                data-testid="route-error-retry"
              >
                <ArrowClockwise size={14} weight="bold" /> Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
